import { Data, NeoModel, NotifyUtils } from '@singularsystems/neo-core';
import { Views } from '@singularsystems/neo-react';
import { reaction } from 'mobx';
import { NotificationDuration } from '../../../App/Models/Enums/NotificationDuration';
import { AppService, Types } from '../../../App/Services/AppService';
import { TextConstants } from '../../../Common/TextConstants';
import CreateRawGenericExportMappingCommand from '../Models/Commands/CreateRawGenericExportMappingCommand';
import UpdateRawGenericExportMappingCommand from '../Models/Commands/UpdateRawGenericExportMappingCommand';
import RawGenericExportMappingSearchCriteria from '../Models/Queries/RawGenericExportMappingSearchCriteria';
import RawGenericExportMappingLookup from '../Models/Lookups/RawGenericExportMappingLookup';
import ManageRawGenericExportMappingComponentVM from './ManageRawGenericExportMappingComponentVM';

@NeoModel
export default class RawGenericExportMappingComponentVM extends Views.ViewModelBase {

    constructor(
        taskRunner = AppService.get(Types.Neo.TaskRunner),
        private notifications = AppService.get(Types.Neo.UI.GlobalNotifications),
        public rawGenericExportMappingApiClient = AppService.get(Types.ApiClients.RawGenericExportMappingApiClient),
        public authorisationService = AppService.get(Types.Neo.Security.AuthorisationService)) {

        super(taskRunner);
    }

    public rawGenericExportMappingSearchCriteria = new RawGenericExportMappingSearchCriteria();
    public manageRawGenericMappingVM = new ManageRawGenericExportMappingComponentVM();
    public showManageMappingsComponent: boolean = false;
    public errorCount: number = 0;
    public commandIsValid: boolean = true;

    public pageManager = new Data.PageManager(this.rawGenericExportMappingSearchCriteria, RawGenericExportMappingLookup,
        this.rawGenericExportMappingApiClient.getRawGenericExportMappingsPagedList, {
        pageSize: 10,
        sortBy: "exportName",
        sortAscending: true,
        fetchInitial: true,
        initialTaskRunner: this.taskRunner,
    })

    public async initialise() {
        reaction(() => this.rawGenericExportMappingSearchCriteria.searchCriteria, () => this.pageManager.refreshData(), { fireImmediately: true, delay: 650 });
    }

    public async addNewGenericMapping() {
        this.showManageMappingsComponent = true;
        await this.manageRawGenericMappingVM.initialise();
        this.manageRawGenericMappingVM.managedGenericMapping = new RawGenericExportMappingLookup();
        this.manageRawGenericMappingVM.staticExportFileSpecId = this.rawGenericExportMappingSearchCriteria.staticExportFileSpecId;
        this.manageRawGenericMappingVM.managedGenericMapping.staticServiceProviderFileGroupingId = this.rawGenericExportMappingSearchCriteria.staticServiceProviderFileGroupingId;
    }

    public async cancel() {
        this.manageRawGenericMappingVM.managedGenericMapping = new RawGenericExportMappingLookup();
        this.resetIndicators();
        await this.pageManager.refreshData();
    }

    public async clearSearch() {
        this.rawGenericExportMappingSearchCriteria.meta.searchCriteria.value = "";
        await this.pageManager.refreshData();
    }

    public async editGenericMapping(genericMapping: RawGenericExportMappingLookup) {
        this.manageRawGenericMappingVM.managedGenericMapping.mapFrom(genericMapping).toJSObject();
        this.showManageMappingsComponent = true;
        this.manageRawGenericMappingVM.isFieldReadOnlyInd = true;
        this.manageRawGenericMappingVM.staticExportFileSpecId = this.rawGenericExportMappingSearchCriteria.staticExportFileSpecId;
        this.manageRawGenericMappingVM.managedGenericMapping.staticServiceProviderFileGroupingId = this.rawGenericExportMappingSearchCriteria.staticServiceProviderFileGroupingId;
        await this.manageRawGenericMappingVM.initialise();
    }

    public resetIndicators() {
        this.showManageMappingsComponent = false;
        this.manageRawGenericMappingVM.isFieldReadOnlyInd = false;
    }

    public async saveGenericMapping() {
        if (this.commandIsValid) {
            this.taskRunner.run(async () => {
                var response = this.manageRawGenericMappingVM.managedGenericMapping.rawGenericExportMappingId > 0
                    ? await this.rawGenericExportMappingApiClient.updateRawGenericExportMapping((this.mapGenericExportMappingToUpdateCommand()!.toJSObject()))
                    : await this.rawGenericExportMappingApiClient.addNewRawGenericExportMapping((this.mapGenericExportMappingToCreateCommand()!.toJSObject()));

                if (response.data.success) {
                    await this.pageManager.refreshData();
                    NotifyUtils.addSuccess(TextConstants.Titles.Success, "Generic mapping has been successfully saved.", NotificationDuration.Standard);
                }
                else {
                    NotifyUtils.add(TextConstants.Titles.Error, response.data.error, "danger" as any, NotificationDuration.Long);
                }
                this.resetIndicators();
            })
        }
    }

    private mapGenericExportMappingToCreateCommand() {
        var command = new CreateRawGenericExportMappingCommand().mapFrom(this.manageRawGenericMappingVM.managedGenericMapping);
        return command;
    }

    private mapGenericExportMappingToUpdateCommand() {
        var command = new UpdateRawGenericExportMappingCommand().mapFrom(this.manageRawGenericMappingVM.managedGenericMapping);
        return command;
    }
}